(() => {
  const ELEMENT_NAME = 'alim-formula-explorer';

  if (window.customElements.get(ELEMENT_NAME)) return;

  class AlimFormulaExplorer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.hotspot = this.querySelector('[data-afe-toggle]');
      this.orbit = this.querySelector('[data-afe-orbit]');
      this.closeButton = this.querySelector('[data-afe-close]');
      this.glass = this.querySelector('[data-afe-glass]');
      this.categoryPanels = [...this.querySelectorAll('[data-afe-category-panel]')];
      this.categoryButtons = [...this.querySelectorAll('[data-afe-category]')];
      this.detailPanels = [...this.querySelectorAll('[data-afe-detail]')];
      this.ingredientButtons = [...this.querySelectorAll('[data-afe-ingredient]')];
      this.status = this.querySelector('[data-afe-status]');
      this.lastCategoryButton = null;
      this.audioContext = null;
      this.transitionTimer = null;
      this.audioTimer = null;

      if (!this.hotspot || !this.orbit || !this.glass) return;

      this.baseDuration = Math.max(180, Number.parseInt(this.dataset.duration || '680', 10));
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.hotspot.addEventListener('click', () => this.toggleExplorer());
      this.closeButton?.addEventListener('click', () => this.closeExplorer(true));

      this.categoryButtons.forEach((button) => {
        button.addEventListener('click', () => this.openCategory(button));
      });

      this.ingredientButtons.forEach((button) => {
        button.addEventListener('click', () => this.openIngredient(button));
      });

      this.querySelectorAll('[data-afe-back]').forEach((button) => {
        button.addEventListener('click', () => this.showCategories(true));
      });

      this.querySelectorAll('[data-afe-detail-close]').forEach((button) => {
        button.addEventListener('click', () => this.showCategories(true));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;
        event.preventDefault();
        if (this.dataset.view === 'categories') this.closeExplorer(true);
        else this.showCategories(true);
      });

      this.setClosedState();
    }

    disconnectedCallback() {
      this.clearTimers();
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(() => {});
      }
    }

    get motionDuration() {
      return this.reduceMotion.matches ? 0 : this.baseDuration;
    }

    clearTimers() {
      window.clearTimeout(this.transitionTimer);
      window.clearTimeout(this.audioTimer);
      this.transitionTimer = null;
      this.audioTimer = null;
    }

    toggleExplorer() {
      if (this.classList.contains('is-closing')) return;
      if (this.classList.contains('is-open')) this.closeExplorer(false);
      else this.openExplorer();
    }

    openExplorer() {
      this.clearTimers();
      this.prepareAudio();
      this.resetPanels();
      this.classList.remove('is-closing', 'is-closing-from-detail', 'is-returning');
      this.dataset.view = 'categories';
      this.classList.add('is-open', 'is-opening');
      this.hotspot.setAttribute('aria-expanded', 'true');
      this.orbit.setAttribute('aria-hidden', 'false');
      this.closeButton?.removeAttribute('tabindex');
      this.closeButton?.setAttribute('aria-hidden', 'false');
      this.categoryButtons.forEach((button) => button.removeAttribute('tabindex'));
      this.announce('Esploratore della formula aperto. Scegli una categoria.');
      this.scheduleMechanicalClick(this.motionDuration);

      this.transitionTimer = window.setTimeout(() => {
        this.classList.remove('is-opening');
        this.transitionTimer = null;
      }, this.motionDuration);
    }

    closeExplorer(returnFocus) {
      if (!this.classList.contains('is-open') || this.classList.contains('is-closing')) return;

      this.clearTimers();
      this.prepareAudio();
      const closesFromDetail = this.dataset.view !== 'categories';
      const closeDuration = this.reduceMotion.matches
        ? 0
        : Math.round(this.baseDuration * (closesFromDetail ? 1.25 : 1));

      this.resetPanels();
      this.classList.remove('is-opening', 'is-returning');
      this.classList.add('is-closing');
      this.classList.toggle('is-closing-from-detail', closesFromDetail);
      this.hotspot.setAttribute('aria-expanded', 'false');
      this.orbit.setAttribute('aria-hidden', 'true');
      this.closeButton?.setAttribute('tabindex', '-1');
      this.closeButton?.setAttribute('aria-hidden', 'true');
      this.categoryButtons.forEach((button) => button.setAttribute('tabindex', '-1'));
      this.scheduleMechanicalClick(closeDuration);

      this.transitionTimer = window.setTimeout(() => {
        this.classList.remove('is-open', 'is-closing', 'is-closing-from-detail');
        this.dataset.view = 'closed';
        this.announce('Esploratore della formula chiuso.');
        this.transitionTimer = null;
        if (returnFocus) this.hotspot.focus({ preventScroll: true });
      }, closeDuration);
    }

    setClosedState() {
      this.classList.remove('is-open', 'is-opening', 'is-closing', 'is-closing-from-detail', 'is-returning');
      this.dataset.view = 'closed';
      this.hotspot.setAttribute('aria-expanded', 'false');
      this.orbit.setAttribute('aria-hidden', 'true');
      this.closeButton?.setAttribute('tabindex', '-1');
      this.closeButton?.setAttribute('aria-hidden', 'true');
      this.categoryButtons.forEach((button) => button.setAttribute('tabindex', '-1'));
      this.resetPanels();
    }

    openCategory(button) {
      if (this.classList.contains('is-closing')) return;
      const key = button.dataset.afeCategory;
      const panel = this.categoryPanels.find((item) => item.dataset.afeCategoryPanel === key);
      if (!panel) return;

      this.prepareAudio();
      this.lastCategoryButton = button;
      this.classList.remove('is-opening', 'is-returning');
      this.categoryPanels.forEach((item) => { item.hidden = item !== panel; });
      this.detailPanels.forEach((item) => { item.hidden = true; });
      this.glass.hidden = false;
      this.dataset.view = 'ingredients';
      this.orbit.setAttribute('aria-hidden', 'true');
      this.categoryButtons.forEach((item) => item.setAttribute('tabindex', '-1'));
      this.scheduleMechanicalClick(0);

      const firstTarget = panel.querySelector('[data-afe-ingredient]') || panel.querySelector('[data-afe-back]');
      firstTarget?.focus({ preventScroll: true });
      this.announce(`${button.dataset.categoryName || 'Categoria'}: elenco degli attivi aperto.`);
    }

    openIngredient(button) {
      if (this.classList.contains('is-closing')) return;
      const id = button.dataset.afeIngredient;
      const detail = this.detailPanels.find((item) => item.dataset.afeDetail === id);
      if (!detail) return;

      this.prepareAudio();
      this.classList.remove('is-opening');
      this.categoryPanels.forEach((item) => { item.hidden = true; });
      this.detailPanels.forEach((item) => { item.hidden = item !== detail; });
      this.glass.hidden = false;
      this.dataset.view = 'detail';
      this.scheduleMechanicalClick(0);

      const close = detail.querySelector('[data-afe-detail-close]');
      close?.focus({ preventScroll: true });
      this.announce(`${button.dataset.ingredientName || 'Attivo'}: scheda aperta.`);
    }

    showCategories(returnFocus) {
      if (!this.classList.contains('is-open') || this.classList.contains('is-closing')) return;

      this.clearTimers();
      this.prepareAudio();
      this.resetPanels();
      this.classList.remove('is-opening');
      this.classList.add('is-returning');
      this.dataset.view = 'categories';
      this.orbit.setAttribute('aria-hidden', 'false');
      this.categoryButtons.forEach((button) => button.removeAttribute('tabindex'));
      this.announce('Scegli una delle cinque macro-categorie.');
      this.scheduleMechanicalClick(this.motionDuration);

      this.transitionTimer = window.setTimeout(() => {
        this.classList.remove('is-returning');
        this.transitionTimer = null;
        if (returnFocus && this.lastCategoryButton) {
          this.lastCategoryButton.focus({ preventScroll: true });
        }
      }, this.motionDuration);
    }

    resetPanels() {
      this.glass.hidden = true;
      this.categoryPanels.forEach((panel) => { panel.hidden = true; });
      this.detailPanels.forEach((panel) => { panel.hidden = true; });
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.setTimeout(() => { this.status.textContent = message; }, 20);
    }

    prepareAudio() {
      if (this.dataset.sound !== 'true') return;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      try {
        if (!this.audioContext) this.audioContext = new AudioContextClass();
        if (this.audioContext.state === 'suspended') this.audioContext.resume().catch(() => {});
      } catch (error) {
        this.audioContext = null;
      }
    }

    scheduleMechanicalClick(delay) {
      window.clearTimeout(this.audioTimer);
      if (!this.audioContext || this.dataset.sound !== 'true') return;
      this.audioTimer = window.setTimeout(() => {
        this.audioTimer = null;
        this.playMechanicalClick();
      }, Math.max(0, delay));
    }

    playMechanicalClick() {
      const context = this.audioContext;
      if (!context || context.state === 'closed') return;

      const volumeSetting = Math.min(100, Math.max(0, Number.parseInt(this.dataset.volume || '35', 10)));
      const level = Math.max(.0001, volumeSetting / 100);
      const trigger = () => {
        const now = context.currentTime;
        const oscillator = context.createOscillator();
        const oscillatorGain = context.createGain();
        const noiseGain = context.createGain();
        const noiseFilter = context.createBiquadFilter();
        const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * .045), context.sampleRate);
        const channel = buffer.getChannelData(0);

        for (let index = 0; index < channel.length; index += 1) {
          channel[index] = (Math.random() * 2 - 1) * (1 - index / channel.length);
        }

        const noise = context.createBufferSource();
        noise.buffer = buffer;
        noiseFilter.type = 'highpass';
        noiseFilter.frequency.setValueAtTime(1350, now);
        noiseGain.gain.setValueAtTime(.1 * level, now);
        noiseGain.gain.exponentialRampToValueAtTime(.0001, now + .045);

        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(230, now);
        oscillator.frequency.exponentialRampToValueAtTime(82, now + .04);
        oscillatorGain.gain.setValueAtTime(.075 * level, now);
        oscillatorGain.gain.exponentialRampToValueAtTime(.0001, now + .045);

        noise.connect(noiseFilter).connect(noiseGain).connect(context.destination);
        oscillator.connect(oscillatorGain).connect(context.destination);
        noise.start(now);
        noise.stop(now + .05);
        oscillator.start(now);
        oscillator.stop(now + .05);
      };

      if (context.state === 'suspended') context.resume().then(trigger).catch(() => {});
      else trigger();
    }
  }

  window.customElements.define(ELEMENT_NAME, AlimFormulaExplorer);
})();
